// src/pages/finance-portal/journal-mappings/JournalMappings.js

import React, { useState, useEffect, useCallback } from 'react';
import {
    Box, Typography, Paper, Button, TextField, Card,
    Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
    Chip, IconButton, Alert, CircularProgress, Skeleton,
    Dialog, DialogTitle, DialogContent, DialogActions,
    FormControlLabel, Switch, Tooltip, Autocomplete, InputAdornment,
} from '@mui/material';
import TransformIcon from '@mui/icons-material/Transform';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import axiosInstance from '../../../utils/axiosInstance';

const fontFamily = "'Plus Jakarta Sans', -apple-system, BlinkMacSystemFont, sans-serif";
const ACCENT = '#22c55e';

function formatCurrency(val) {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(val || 0);
}

const EVENT_TYPES = [
    'invoice_created',
    'payment_received',
    'credit_memo_applied',
    'expense_recorded',
    'refund_issued',
    'adjustment_posted',
];

const emptyMapping = { event_type: '', debit_account_id: null, credit_account_id: null, description_template: '', is_active: true };

export default function JournalMappings() {
    const [mappings, setMappings] = useState([]);
    const [accounts, setAccounts] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(null);

    // Dialog
    const [dialog, setDialog] = useState({ open: false, editing: null });
    const [form, setForm] = useState(emptyMapping);
    const [saving, setSaving] = useState(false);

    // Delete
    const [deleteDialog, setDeleteDialog] = useState({ open: false, mapping: null });
    const [deleting, setDeleting] = useState(false);

    // Test generate
    const [testDialog, setTestDialog] = useState(false);
    const [testForm, setTestForm] = useState({ event_type: '', amount: '', reference: '' });
    const [testResult, setTestResult] = useState(null);
    const [testing, setTesting] = useState(false);

    const fetchMappings = useCallback(async () => {
        setIsLoading(true);
        setError(null);
        try {
            const res = await axiosInstance.get('/api/finance/journal-mappings');
            if (res.data.success) {
                setMappings(res.data.data);
            }
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to load journal mappings');
        } finally {
            setIsLoading(false);
        }
    }, []);

    const fetchAccounts = useCallback(async () => {
        try {
            const res = await axiosInstance.get('/api/finance/coa', { params: { flat: true } });
            if (res.data.success) {
                setAccounts(res.data.data);
            }
        } catch (err) {
            // silently fail, accounts will be empty
        }
    }, []);

    useEffect(() => { fetchMappings(); fetchAccounts(); }, [fetchMappings, fetchAccounts]);

    const getAccountLabel = (acct) => acct ? `${acct.account_number} - ${acct.name}` : '';

    const findAccount = (id) => accounts.find(a => a.id === id) || null;

    const openDialog = (mapping = null) => {
        if (mapping) {
            setForm({
                event_type: mapping.event_type,
                debit_account_id: mapping.debit_account_id,
                credit_account_id: mapping.credit_account_id,
                description_template: mapping.description_template || '',
                is_active: mapping.is_active,
            });
        } else {
            setForm(emptyMapping);
        }
        setDialog({ open: true, editing: mapping });
    };

    const saveMapping = async () => {
        setSaving(true);
        try {
            if (dialog.editing) {
                await axiosInstance.put(`/api/finance/journal-mappings/${dialog.editing.id}`, form);
                setSuccess('Mapping updated');
            } else {
                await axiosInstance.post('/api/finance/journal-mappings', form);
                setSuccess('Mapping created');
            }
            setDialog({ open: false, editing: null });
            fetchMappings();
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to save mapping');
        } finally {
            setSaving(false);
        }
    };

    const handleDelete = async () => {
        setDeleting(true);
        try {
            await axiosInstance.delete(`/api/finance/journal-mappings/${deleteDialog.mapping.id}`);
            setSuccess('Mapping deleted');
            setDeleteDialog({ open: false, mapping: null });
            fetchMappings();
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to delete mapping');
        } finally {
            setDeleting(false);
        }
    };

    const handleTestGenerate = async () => {
        setTesting(true);
        setTestResult(null);
        try {
            const res = await axiosInstance.post('/api/finance/journal-mappings/generate', {
                event_type: testForm.event_type,
                amount: parseFloat(testForm.amount) || 0,
                reference: testForm.reference,
            });
            if (res.data.success) {
                setTestResult(res.data.data);
            }
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to generate test entry');
        } finally {
            setTesting(false);
        }
    };

    return (
        <Box sx={{ minHeight: '100vh', bgcolor: '#f8fafc' }}>
            {/* Header */}
            <Paper elevation={0} sx={{
                background: 'linear-gradient(135deg, #22c55e 0%, #16a34a 100%)',
                borderRadius: { xs: 0, md: '0 0 24px 24px' },
                px: { xs: 2.5, md: 4 }, py: { xs: 3, md: 4 }, mb: 3,
                position: 'relative', overflow: 'hidden',
            }}>
                <Box sx={{ position: 'absolute', top: -30, right: -30, width: 160, height: 160, borderRadius: '50%', bgcolor: 'rgba(255,255,255,0.06)' }} />
                <Box sx={{ position: 'relative', zIndex: 1, display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <Box sx={{ p: 1.5, borderRadius: '14px', bgcolor: 'rgba(255,255,255,0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <TransformIcon sx={{ fontSize: 28, color: '#fff' }} />
                        </Box>
                        <Box>
                            <Typography variant="h4" sx={{ fontFamily, fontWeight: 800, color: 'white', fontSize: { xs: '1.4rem', md: '1.85rem' } }}>Journal Mappings</Typography>
                            <Typography sx={{ fontFamily, color: 'rgba(255,255,255,0.8)', fontSize: '0.9rem', mt: 0.25 }}>Auto-journal event mappings</Typography>
                        </Box>
                    </Box>
                    <Button variant="contained" startIcon={<AddIcon />} onClick={() => openDialog()}
                        sx={{ fontFamily, fontWeight: 600, textTransform: 'none', borderRadius: '10px', bgcolor: 'rgba(255,255,255,0.2)', '&:hover': { bgcolor: 'rgba(255,255,255,0.3)' }, color: '#fff', boxShadow: 'none' }}
                    >
                        Add Mapping
                    </Button>
                </Box>
            </Paper>

            <Box sx={{ px: { xs: 2, md: 4 }, pb: 4, maxWidth: 1400, mx: 'auto' }}>
                {error && <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 2, borderRadius: '12px', fontFamily }}>{error}</Alert>}
                {success && <Alert severity="success" onClose={() => setSuccess(null)} sx={{ mb: 2, borderRadius: '12px', fontFamily }}>{success}</Alert>}

                {/* Toolbar */}
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
                    <Button variant="outlined" startIcon={<PlayArrowIcon />} onClick={() => { setTestDialog(true); setTestResult(null); setTestForm({ event_type: '', amount: '', reference: '' }); }}
                        sx={{ fontFamily, fontWeight: 600, textTransform: 'none', borderRadius: '10px', borderColor: ACCENT, color: ACCENT, '&:hover': { borderColor: '#16a34a', bgcolor: `${ACCENT}08` } }}
                    >
                        Test Generate
                    </Button>
                </Box>

                {/* Table */}
                <Card sx={{ borderRadius: '16px', border: '1px solid #e2e8f0', boxShadow: 'none' }}>
                    <TableContainer>
                        <Table size="small">
                            <TableHead>
                                <TableRow sx={{ bgcolor: '#f8fafc' }}>
                                    {['Event Type', 'Debit Account', 'Credit Account', 'Description Template', 'Active', 'Actions'].map(h => (
                                        <TableCell key={h} sx={{ fontFamily, fontWeight: 700, fontSize: '0.75rem', color: '#64748b', textTransform: 'uppercase', borderBottom: '2px solid #e2e8f0' }}>{h}</TableCell>
                                    ))}
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {isLoading ? (
                                    [1, 2, 3, 4].map(i => <TableRow key={i}><TableCell colSpan={6}><Skeleton height={36} /></TableCell></TableRow>)
                                ) : mappings.length === 0 ? (
                                    <TableRow><TableCell colSpan={6} sx={{ textAlign: 'center', py: 4 }}>
                                        <Typography sx={{ fontFamily, color: '#94a3b8' }}>No journal mappings configured</Typography>
                                    </TableCell></TableRow>
                                ) : mappings.map(m => (
                                    <TableRow key={m.id} hover sx={{ '&:hover': { bgcolor: '#f8fafc' } }}>
                                        <TableCell>
                                            <Chip label={m.event_type} size="small" sx={{ fontFamily, fontSize: '0.75rem', fontWeight: 600, height: 24, bgcolor: `${ACCENT}15`, color: ACCENT }} />
                                        </TableCell>
                                        <TableCell sx={{ fontFamily, fontSize: '0.85rem', color: '#475569' }}>
                                            {m.debit_account_name || m.debit_account_number || '—'}
                                        </TableCell>
                                        <TableCell sx={{ fontFamily, fontSize: '0.85rem', color: '#475569' }}>
                                            {m.credit_account_name || m.credit_account_number || '—'}
                                        </TableCell>
                                        <TableCell sx={{ fontFamily, fontSize: '0.85rem', color: '#475569', maxWidth: 250, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                            {m.description_template || '—'}
                                        </TableCell>
                                        <TableCell>
                                            <Chip label={m.is_active ? 'Active' : 'Inactive'} size="small"
                                                sx={{ fontFamily, fontSize: '0.65rem', fontWeight: 600, height: 20, bgcolor: m.is_active ? '#dcfce7' : '#f1f5f9', color: m.is_active ? '#166534' : '#64748b' }}
                                            />
                                        </TableCell>
                                        <TableCell>
                                            <Box sx={{ display: 'flex', gap: 0.5 }}>
                                                <Tooltip title="Edit"><IconButton size="small" onClick={() => openDialog(m)} sx={{ color: '#94a3b8', '&:hover': { color: '#3b82f6' } }}><EditIcon fontSize="small" /></IconButton></Tooltip>
                                                <Tooltip title="Delete"><IconButton size="small" onClick={() => setDeleteDialog({ open: true, mapping: m })} sx={{ color: '#94a3b8', '&:hover': { color: '#ef4444' } }}><DeleteIcon fontSize="small" /></IconButton></Tooltip>
                                            </Box>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </Card>
            </Box>

            {/* Add/Edit Mapping Dialog */}
            <Dialog open={dialog.open} onClose={() => setDialog({ open: false, editing: null })} maxWidth="sm" fullWidth
                PaperProps={{ sx: { borderRadius: '16px', fontFamily } }}
            >
                <DialogTitle sx={{ fontFamily, fontWeight: 700, fontSize: '1.1rem' }}>
                    {dialog.editing ? 'Edit Mapping' : 'Add Mapping'}
                </DialogTitle>
                <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2.5, pt: '16px !important' }}>
                    <Autocomplete
                        freeSolo
                        options={EVENT_TYPES}
                        value={form.event_type}
                        onInputChange={(_, val) => setForm(prev => ({ ...prev, event_type: val }))}
                        renderInput={(params) => (
                            <TextField {...params} label="Event Type" size="small"
                                InputProps={{ ...params.InputProps, sx: { fontFamily, borderRadius: '10px' } }}
                                InputLabelProps={{ sx: { fontFamily } }}
                            />
                        )}
                    />
                    <Autocomplete
                        options={accounts}
                        value={findAccount(form.debit_account_id)}
                        onChange={(_, val) => setForm(prev => ({ ...prev, debit_account_id: val?.id || null }))}
                        getOptionLabel={getAccountLabel}
                        isOptionEqualToValue={(opt, val) => opt.id === val?.id}
                        renderInput={(params) => (
                            <TextField {...params} label="Debit Account" size="small"
                                InputProps={{ ...params.InputProps, sx: { fontFamily, borderRadius: '10px' } }}
                                InputLabelProps={{ sx: { fontFamily } }}
                            />
                        )}
                        renderOption={(props, option) => (
                            <li {...props} key={option.id}>
                                <Typography sx={{ fontFamily, fontSize: '0.85rem' }}>{getAccountLabel(option)}</Typography>
                            </li>
                        )}
                    />
                    <Autocomplete
                        options={accounts}
                        value={findAccount(form.credit_account_id)}
                        onChange={(_, val) => setForm(prev => ({ ...prev, credit_account_id: val?.id || null }))}
                        getOptionLabel={getAccountLabel}
                        isOptionEqualToValue={(opt, val) => opt.id === val?.id}
                        renderInput={(params) => (
                            <TextField {...params} label="Credit Account" size="small"
                                InputProps={{ ...params.InputProps, sx: { fontFamily, borderRadius: '10px' } }}
                                InputLabelProps={{ sx: { fontFamily } }}
                            />
                        )}
                        renderOption={(props, option) => (
                            <li {...props} key={option.id}>
                                <Typography sx={{ fontFamily, fontSize: '0.85rem' }}>{getAccountLabel(option)}</Typography>
                            </li>
                        )}
                    />
                    <TextField label="Description Template" value={form.description_template}
                        onChange={e => setForm(prev => ({ ...prev, description_template: e.target.value }))}
                        fullWidth size="small" multiline rows={2}
                        helperText="Use {amount}, {reference}, {date} as placeholders"
                        InputProps={{ sx: { fontFamily, borderRadius: '10px' } }}
                        InputLabelProps={{ sx: { fontFamily } }}
                        FormHelperTextProps={{ sx: { fontFamily, fontSize: '0.75rem' } }}
                    />
                    <FormControlLabel
                        control={<Switch checked={form.is_active} onChange={e => setForm(prev => ({ ...prev, is_active: e.target.checked }))}
                            sx={{ '& .MuiSwitch-switchBase.Mui-checked': { color: ACCENT }, '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { bgcolor: ACCENT } }}
                        />}
                        label={<Typography sx={{ fontFamily, fontSize: '0.9rem' }}>Active</Typography>}
                    />
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2.5 }}>
                    <Button onClick={() => setDialog({ open: false, editing: null })} sx={{ fontFamily, textTransform: 'none', color: '#64748b' }}>Cancel</Button>
                    <Button variant="contained" onClick={saveMapping} disabled={saving || !form.event_type || !form.debit_account_id || !form.credit_account_id}
                        sx={{ fontFamily, fontWeight: 600, textTransform: 'none', borderRadius: '10px', bgcolor: ACCENT, '&:hover': { bgcolor: '#16a34a' } }}
                    >
                        {saving ? <CircularProgress size={20} sx={{ color: '#fff' }} /> : dialog.editing ? 'Update' : 'Create'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Delete Confirmation */}
            <Dialog open={deleteDialog.open} onClose={() => setDeleteDialog({ open: false, mapping: null })}
                PaperProps={{ sx: { borderRadius: '16px', fontFamily } }}
            >
                <DialogTitle sx={{ fontFamily, fontWeight: 700, fontSize: '1.1rem' }}>Confirm Delete</DialogTitle>
                <DialogContent>
                    <Typography sx={{ fontFamily, fontSize: '0.9rem', color: '#475569' }}>
                        Are you sure you want to delete the mapping for <strong>{deleteDialog.mapping?.event_type}</strong>? This action cannot be undone.
                    </Typography>
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2.5 }}>
                    <Button onClick={() => setDeleteDialog({ open: false, mapping: null })} sx={{ fontFamily, textTransform: 'none', color: '#64748b' }}>Cancel</Button>
                    <Button variant="contained" onClick={handleDelete} disabled={deleting}
                        sx={{ fontFamily, fontWeight: 600, textTransform: 'none', borderRadius: '10px', bgcolor: '#ef4444', '&:hover': { bgcolor: '#dc2626' } }}
                    >
                        {deleting ? <CircularProgress size={20} sx={{ color: '#fff' }} /> : 'Delete'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Test Generate Dialog */}
            <Dialog open={testDialog} onClose={() => setTestDialog(false)} maxWidth="sm" fullWidth
                PaperProps={{ sx: { borderRadius: '16px', fontFamily } }}
            >
                <DialogTitle sx={{ fontFamily, fontWeight: 700, fontSize: '1.1rem' }}>Test Generate Journal Entry</DialogTitle>
                <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2.5, pt: '16px !important' }}>
                    <Autocomplete
                        freeSolo
                        options={EVENT_TYPES}
                        value={testForm.event_type}
                        onInputChange={(_, val) => setTestForm(prev => ({ ...prev, event_type: val }))}
                        renderInput={(params) => (
                            <TextField {...params} label="Event Type" size="small"
                                InputProps={{ ...params.InputProps, sx: { fontFamily, borderRadius: '10px' } }}
                                InputLabelProps={{ sx: { fontFamily } }}
                            />
                        )}
                    />
                    <TextField label="Amount" value={testForm.amount}
                        onChange={e => setTestForm(prev => ({ ...prev, amount: e.target.value }))}
                        fullWidth size="small" type="number"
                        InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment>, sx: { fontFamily, borderRadius: '10px' } }}
                        InputLabelProps={{ sx: { fontFamily } }}
                    />
                    <TextField label="Reference" value={testForm.reference}
                        onChange={e => setTestForm(prev => ({ ...prev, reference: e.target.value }))}
                        fullWidth size="small"
                        InputProps={{ sx: { fontFamily, borderRadius: '10px' } }}
                        InputLabelProps={{ sx: { fontFamily } }}
                    />
                    <Button variant="contained" onClick={handleTestGenerate} disabled={testing || !testForm.event_type || !testForm.amount}
                        startIcon={testing ? <CircularProgress size={16} sx={{ color: '#fff' }} /> : <PlayArrowIcon />}
                        sx={{ fontFamily, fontWeight: 600, textTransform: 'none', borderRadius: '10px', bgcolor: ACCENT, '&:hover': { bgcolor: '#16a34a' }, alignSelf: 'flex-start' }}
                    >
                        Generate
                    </Button>

                    {testResult && (
                        <Card sx={{ borderRadius: '12px', border: '1px solid #e2e8f0', boxShadow: 'none', p: 2, bgcolor: '#f8fafc' }}>
                            <Typography sx={{ fontFamily, fontWeight: 700, fontSize: '0.9rem', color: '#1e293b', mb: 1 }}>Generated Entry Preview</Typography>
                            <Typography sx={{ fontFamily, fontSize: '0.85rem', color: '#475569', mb: 0.5 }}>
                                <strong>Description:</strong> {testResult.description}
                            </Typography>
                            {testResult.lines && testResult.lines.map((line, idx) => (
                                <Box key={idx} sx={{ display: 'flex', gap: 2, py: 0.5, borderBottom: '1px solid #e2e8f0' }}>
                                    <Typography sx={{ fontFamily, fontSize: '0.8rem', color: '#475569', flex: 1 }}>{line.account_name || line.account_number}</Typography>
                                    <Typography sx={{ fontFamily, fontSize: '0.8rem', fontWeight: 600, color: '#166534' }}>{line.debit ? formatCurrency(line.debit) : ''}</Typography>
                                    <Typography sx={{ fontFamily, fontSize: '0.8rem', fontWeight: 600, color: '#991b1b' }}>{line.credit ? formatCurrency(line.credit) : ''}</Typography>
                                </Box>
                            ))}
                        </Card>
                    )}
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2.5 }}>
                    <Button onClick={() => setTestDialog(false)} sx={{ fontFamily, textTransform: 'none', color: '#64748b' }}>Close</Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}
